import React from 'react';
import { useNavigate } from 'react-router';
import { X } from 'lucide-react';
import type { AnnouncementBanner as AnnouncementBannerType } from '@shared/types';
import { useAnnouncements } from '../../hooks/useAnnouncements';

const colorSchemes = {
  stream: {
    bg: 'bg-stream',
    text: 'text-growth',
    closeHover: 'hover:bg-growth/10',
    linkStyle: 'text-growth underline underline-offset-2 font-medium',
  },
  sky: {
    bg: 'bg-sky',
    text: 'text-cloud',
    closeHover: 'hover:bg-cloud/10',
    linkStyle: 'text-cloud underline underline-offset-2 font-medium',
  },
} as const;

const BannerItem: React.FC<{
  announcement: AnnouncementBannerType;
  onDismiss: (id: string) => void;
  isFirst?: boolean;
}> = ({ announcement, onDismiss, isFirst }) => {
  const navigate = useNavigate();
  const scheme = colorSchemes[announcement.colorScheme] || colorSchemes.stream;

  const handleLinkClick = () => {
    if (!announcement.linkUrl) return;
    if (announcement.linkUrl.startsWith('http')) {
      window.open(announcement.linkUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(announcement.linkUrl);
    }
  };

  return (
    <div
      className={`${scheme.bg} ${scheme.text} px-4 py-3 relative`}
      style={isFirst ? { paddingTop: `max(0.75rem, var(--sat))` } : undefined}
    >
      <div className="flex items-start gap-3 max-w-4xl mx-auto">
        <div className="flex-1 min-w-0 text-sm font-sans text-center">
          <span className="font-semibold">{announcement.title}</span>
          {announcement.body && (
            <span className="mx-2 opacity-90">{announcement.body}</span>
          )}
          {announcement.linkText && announcement.linkUrl && (
            <button
              onClick={handleLinkClick}
              className={`mx-2 ${scheme.linkStyle} inline`}
            >
              {announcement.linkText}
            </button>
          )}
        </div>
        <button
          onClick={() => onDismiss(announcement.id)}
          className={`shrink-0 p-1 rounded-full ${scheme.closeHover} transition-colors`}
          aria-label="Dismiss announcement"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const AnnouncementBanners: React.FC<{ hasBannerAbove?: boolean }> = ({ hasBannerAbove }) => {
  const { announcements, dismiss } = useAnnouncements();

  if (announcements.length === 0) return null;

  return (
    <div className="w-full">
      {announcements.map((a, i) => (
        <BannerItem key={a.id} announcement={a} onDismiss={dismiss} isFirst={i === 0 && !hasBannerAbove} />
      ))}
    </div>
  );
};
